/**
 * Dark theme — night-mode for long audit sessions. Deep slate
 * background, soft-white text. Same component overrides as modern
 * (rounded corners, generous padding) so the UI muscle memory holds.
 */
import { createTheme, alpha } from '@mui/material/styles';

const D = {
  primary: '#60A5FA',
  primaryDark: '#2563EB',
  primaryLight: '#1E3A8A',
  bg: '#0F172A',
  surface: '#1E293B',
  surfaceAlt: '#172033',
  surfaceHover: '#243047',
  border: '#334155',
  borderLight: '#1F2A3D',
  text: '#F1F5F9',
  textSecondary: '#94A3B8',
};

const baseTypography = {
  fontFamily: '"Rubik", "Heebo", "Inter", system-ui, -apple-system, sans-serif',
  h1: { fontWeight: 700, letterSpacing: '-0.022em' },
  h2: { fontWeight: 700, letterSpacing: '-0.02em' },
  h3: { fontWeight: 700, letterSpacing: '-0.018em' },
  h4: { fontWeight: 700, letterSpacing: '-0.015em' },
  h5: { fontWeight: 600, letterSpacing: '-0.01em' },
  h6: { fontWeight: 600, fontSize: '1.05rem', letterSpacing: '-0.006em' },
  subtitle1: { fontWeight: 600, fontSize: '0.95rem' },
  subtitle2: { fontWeight: 600, fontSize: '0.8rem', textTransform: 'uppercase' as const, letterSpacing: '0.04em' },
  body1: { fontSize: '0.875rem', lineHeight: 1.6 },
  body2: { fontSize: '0.8125rem', lineHeight: 1.55 },
  caption: { fontSize: '0.75rem' },
};

const sharedDark = {
  typography: baseTypography,
  palette: {
    mode: 'dark' as const,
    primary: { main: D.primary, dark: D.primaryDark, light: D.primaryLight },
    background: { default: D.bg, paper: D.surface },
    text: { primary: D.text, secondary: D.textSecondary },
    divider: D.border,
  },
  shape: { borderRadius: 10 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: D.bg,
          backgroundImage: 'none',
          WebkitFontSmoothing: 'antialiased',
          textRendering: 'optimizeLegibility' as const,
          colorScheme: 'dark' as const,
          scrollbarWidth: 'thin' as const,
          scrollbarColor: '#475569 transparent',
        },
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            transitionDuration: '0.01ms !important',
            scrollBehavior: 'auto !important',
          },
        },
        '[dir="rtl"] .rtl-flip': { transform: 'scaleX(-1)' },
        '.cv-rows tbody tr': {
          contentVisibility: 'auto',
          containIntrinsicSize: 'auto 41px',
        },
        '.num, .tabular': {
          fontVariantNumeric: 'tabular-nums',
          fontFeatureSettings: '"tnum" 1, "cv11" 1',
        },
        '.total-row > td, .total-row > th': {
          fontWeight: 700,
          borderTop: `1px solid ${D.border}`,
          backgroundColor: D.surfaceAlt,
        },
        '*::-webkit-scrollbar': { width: 8, height: 8 },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: '#475569', borderRadius: 4,
          border: '2px solid transparent', backgroundClip: 'padding-box',
        },
        '*::-webkit-scrollbar-thumb:hover': { backgroundColor: '#64748B' },
        '*::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 10, fontWeight: 600, fontSize: '0.875rem',
          textTransform: 'none' as const, padding: '8px 18px',
        },
        outlined: {
          borderColor: D.border, color: D.text,
          '&:hover': { backgroundColor: D.surfaceHover, borderColor: D.primary },
        },
        text: { '&:hover': { backgroundColor: alpha(D.primary, 0.10) } },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          '&:hover': { backgroundColor: alpha(D.primary, 0.12) },
        },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined' as const },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10, fontSize: '0.875rem',
            backgroundColor: D.surfaceAlt,
            '& fieldset': { borderColor: D.border },
            '&:hover fieldset': { borderColor: D.primary },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${D.border}`,
          backgroundColor: D.surface,
          boxShadow: '0 1px 3px 0 rgba(0,0,0,0.30)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: { root: { borderRadius: 12, backgroundImage: 'none' } },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, fontSize: '0.75rem', height: 26, borderRadius: 8 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: 'none' as const, fontWeight: 600, fontSize: '0.875rem', minHeight: 44 },
      },
    },
    MuiAppBar: {
      defaultProps: { color: 'inherit' as const, elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: D.surface,
          backgroundImage: 'linear-gradient(180deg, #1E293B 0%, #172033 100%)',
          color: D.text,
          borderBottom: `1px solid ${D.border}`,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            fontWeight: 700, fontSize: '0.78rem',
            color: D.textSecondary, textTransform: 'uppercase' as const, letterSpacing: '0.04em',
            backgroundColor: D.surfaceAlt,
            borderBottom: `1px solid ${D.border}`,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem', borderColor: D.borderLight, padding: '12px 16px',
          fontVariantNumeric: 'tabular-nums', fontFeatureSettings: '"tnum" 1',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: { root: { '&:hover': { backgroundColor: D.surfaceHover } } },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          backgroundColor: D.surface,
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.55)',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12, marginTop: 4,
          backgroundColor: D.surface,
          border: `1px solid ${D.border}`,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem', borderRadius: 8, margin: '2px 4px',
          '&:hover': { backgroundColor: D.surfaceHover },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '0.75rem', fontWeight: 600, background: '#F1F5F9',
          color: '#0F172A',
          padding: '6px 10px', borderRadius: 8,
        },
        arrow: { color: '#F1F5F9' },
      },
    },
  },
};

export const darkRtlTheme = createTheme({ ...sharedDark, direction: 'rtl' });
export const darkLtrTheme = createTheme({ ...sharedDark, direction: 'ltr' });

export const DARK_SIDEBAR_COLORS = {
  background:      D.surface,
  backgroundLight: D.surfaceAlt,
  text:            D.text,
  textSecondary:   D.textSecondary,
  activeItem:      alpha(D.primary, 0.16),
  hoverItem:       D.surfaceHover,
  activeText:      D.primary,
  border:          D.border,
};
